import type { TemplatePackage } from '@/features/survey/engine';
import type { PackageDocumentRecord, TemplateVersionRecord } from '@/shared/offline';

import { toEnginePackage } from './adapter';
import { apiTemplatePackageSchema } from './types';

/**
 * Het enginepakket van een lokaal bewaarde templateversie. Het document wordt
 * één keer per versie en checksum gevalideerd en vertaald; daarna komt het uit
 * het geheugen - een pakket van 4,6 MB parsen hoort niet bij elke render.
 */
const cache = new Map<string, TemplatePackage>();

export type EnginePackageResult =
    | { status: 'ready'; pkg: TemplatePackage }
    | { status: 'missing' }
    | { status: 'invalid'; error: unknown };

export function enginePackageFor(
    record: TemplateVersionRecord | undefined,
    stored: PackageDocumentRecord | undefined,
): EnginePackageResult {
    if (!record || !stored || record.fetchedAt === null) {
        return { status: 'missing' };
    }

    const key = `${record.uuid}:${record.etag ?? record.checksum}`;
    const cached = cache.get(key);

    if (cached) {
        return { status: 'ready', pkg: cached };
    }

    const parsed = apiTemplatePackageSchema.safeParse(stored.document);

    if (!parsed.success) {
        return { status: 'invalid', error: parsed.error };
    }

    const pkg = toEnginePackage(parsed.data);
    cache.set(key, pkg);

    return { status: 'ready', pkg };
}
